/* The access gate, lifted from the single-file build.
 *
 * This IS authentication now. It was not before: the codes, the master
 * password and the demo-link signing key all lived in src/lib/accessGate.js
 * and were downloaded by every visitor, so the check could be read, forged or
 * skipped from the sources panel. They have moved to the backend's
 * environment. This component renders the form, accessGate.js posts what was
 * typed, and the backend decides — then refuses every other route to anyone
 * without the session it issued.
 *
 * The first attempt at this component was hand-written around a React state
 * machine calling window.spCheckUrl and window.spRedeemCode. Neither name
 * exists: the module's entry points are spCheckUrl() and spVerify(), and they
 * are plain functions, not window properties. So the invented redeemer was
 * always undefined and EVERY code was rejected, including the valid ones.
 *
 * This renders the original markup and lets the module drive it, the same way
 * the rest of the app works. spVerify reads #spCode, writes #spError and hides
 * #spGate by adding .hidden — which is why the gate is always rendered rather
 * than conditionally: .sp-gate is a fixed overlay and .sp-gate.hidden is
 * display:none.
 */
import { useEffect } from 'react';
import * as gate from '../lib/accessGate.js';
import { entraConfigured } from '../lib/session.js';

export default function AccessGate({ children }) {
  useEffect(() => {
    // The module already knows what to bind — nine handlers it used to attach
    // at parse time, now queued because React mounts this markup. spWireGate
    // binds the queue and is idempotent, so hand-wiring here would only be a
    // second, divergent copy of the same list.
    try { gate.spWireGate(); } catch { /* the gate is optional in a test DOM */ }

    // A ?demo= token opens the gate without a code, or reveals the expired
    // panel. Runs after the markup exists, because it toggles both. It is a
    // round trip to the server now, so it returns a promise: a rejection here
    // would otherwise be an unhandled one, and the gate stays shut either way.
    Promise.resolve()
      .then(() => gate.spCheckUrl())
      .catch(() => { /* no token, or one the server would not honour */ });

    const code = document.getElementById('spCode');
    if (code) code.focus();
  }, []);

  return (
    <>
      {/* ===== ACCESS CONTROL: GATE ===== */}
      <div className="sp-gate" id="spGate">
        <div className="sp-gate-card">
          <div className="sp-gate-logo">
            <svg viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg"><rect x="2" y="2" width="28" height="28" rx="4" stroke="#66B2FF" strokeWidth="2" fill="none"></rect><path d="M8 24V12l8-6 8 6v12" stroke="#003366" strokeWidth="1.5" fill="none" strokeLinejoin="round"></path><circle cx="16" cy="15" r="3" stroke="#003366" strokeWidth="1.5" fill="none"></circle><line x1="16" y1="18" x2="16" y2="24" stroke="#003366" strokeWidth="1.5"></line><line x1="12" y1="21" x2="20" y2="21" stroke="#66B2FF" strokeWidth="1" strokeDasharray="2 2"></line><circle cx="16" cy="15" r="1" fill="#66B2FF"></circle></svg>
            <span>SentriPol™</span>
          </div>
          <div className="sp-gate-sub">AI-Powered Insurance Policy Analysis Engine</div>
          <div id="spGateLogin">
            <span className="sp-gate-badge">AUTHORIZED ACCESS ONLY</span>
            {/* Microsoft Entra sign-in is the main way in when it is configured;
                the access code below stays as a fallback. */}
            {entraConfigured() && (
              <>
                <button className="sp-gate-btn sp-gate-btn--microsoft" id="spMicrosoftBtn" type="button">
                  <svg aria-hidden="true" width="18" height="18" viewBox="0 0 21 21"><rect x="1" y="1" width="9" height="9" fill="#f25022" /><rect x="11" y="1" width="9" height="9" fill="#7fba00" /><rect x="1" y="11" width="9" height="9" fill="#00a4ef" /><rect x="11" y="11" width="9" height="9" fill="#ffb900" /></svg>
                  Sign in with Microsoft
                </button>
                <div className="sp-gate-or"><span>or use an access code</span></div>
              </>
            )}
            <div className="sp-gate-group">
              <label htmlFor="spCode">Access Code</label>
              <input type="password" id="spCode" className="sp-gate-input" placeholder="Enter your access code" autoComplete="off" />
            </div>
            <button className="sp-gate-btn" id="spVerifyBtn">Access SentriPol™</button>
            <div className="sp-gate-error" id="spError"></div>
          </div>
          <div id="spGateAdmin" style={{display: 'none'}}>
            <span className="sp-gate-badge" style={{background: '#FEF3C7', color: '#92400E'}}>ADMINISTRATOR</span>
            <div className="sp-gate-group">
              <label htmlFor="spMaster">Master Password</label>
              <input type="password" id="spMaster" className="sp-gate-input" placeholder="Enter master password" autoComplete="off" />
            </div>
            <button className="sp-gate-btn" id="spAdminLoginBtn">Sign In as Administrator</button>
            <div className="sp-gate-error" id="spAdminError"></div>
          </div>
          <button className="sp-gate-admin-link" id="spModeToggle">Administrator Access</button>
          <div className="sp-gate-footer">© 2026 AI Blueprints. All rights reserved.<br />SentriPol™ is proprietary. Unauthorized access prohibited.<br /><a href="mailto:dayna.stewart@gmail.com">dayna.stewart@gmail.com</a> | 703-307-3469</div>
        </div>
      </div>

      {/* ===== ACCESS CONTROL: EXPIRED ===== */}
      <div className="sp-expired hidden" id="spExpired">
        <div className="sp-expired-card">
          <div style={{fontSize: '48px', marginBottom: '16px'}}>⏰</div>
          <h2>Demo Access Expired</h2>
          <p>This demo link has expired. Please contact Dayna Stewart for renewed access.</p>
          <p style={{marginTop: '16px'}}><a href="mailto:dayna.stewart@gmail.com">dayna.stewart@gmail.com</a> | 703-307-3469</p>
        </div>
      </div>

      {/* ===== ACCESS CONTROL: SESSION BAR ===== */}
      <div className="sp-session hidden" id="spSession">
        <span id="spSessionLabel"></span>
        <span id="spSessionTimer" style={{fontWeight: '700'}}></span>
        <button id="spLogoutBtn">End Session</button>
      </div>

      {/* ===== ACCESS CONTROL: ADMIN PANEL ===== */}
      <div className="sp-admin hidden" id="spAdmin">
        <div className="sp-admin-card">
          <button className="sp-admin-close" id="spAdminCloseBtn">×</button>
          <h2>SentriPol™ Access Manager</h2>
          <div className="sp-admin-sub">Generate demo links for brokers and investors. Access codes are managed in the app configuration.</div>

          <div className="sp-admin-section">
            <h3>Current Access Codes</h3>
            <p style={{fontSize: '12px', color: '#7a7a8a', marginBottom: '8px'}}>These codes are built into the app. To change them, update the configuration and redeploy.</p>
            <div className="sp-admin-codes" id="spAdminCodes"></div>
          </div>

          <div className="sp-admin-section">
            <h3>Generate Time-Limited Demo Link</h3>
            <p style={{fontSize: '12px', color: '#7a7a8a', marginBottom: '10px'}}>Create a unique URL that auto-expires. No access code needed — the link is the access.</p>
            <div className="sp-admin-row">
              <div className="sp-gate-group">
                <label>Recipient</label>
                <input type="text" id="spLinkLabel" className="sp-gate-input" placeholder="e.g., Broker XYZ" />
              </div>
              <div className="sp-gate-group" style={{maxWidth: '120px'}}>
                <label>Days Valid</label>
                <input type="number" id="spLinkDays" className="sp-gate-input" defaultValue="3" min="1" max="90" />
              </div>
              <button className="sp-gate-btn" id="spGenLinkBtn">Generate</button>
            </div>
            <div id="spLinkOutput"></div>
          </div>
        </div>
      </div>



      <div className="toast-container" id="toastContainer"></div>
      {children}
    </>
  );
}
